import { useMemo } from 'react';
import { useParams, useLocation, useNavigate, NavigateFunction } from 'react-router-dom';
import queryString from 'query-string';
import { Location } from 'history';

export interface UseRouter {
  push: (path: string | PushParams) => void;
  replace: (path: string | PushParams) => void;
  pathname: string;
  query: any;
  location: Location;
  history: NavigateFunction;
}
export type PushParams = {
  pathname: string;
  query: { [key: string]: string };
};

export function useRouter(): UseRouter {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Return our custom router object
  // Memoize so that a new object is only returned if something changes
  return useMemo(() => {
    function replace(pathname: string | PushParams) {
      push(pathname, true);
    }

    function push(pathname: string | PushParams, replace = false) {
      if (typeof pathname === 'string') {
        navigate(pathname, { replace });
      } else {
        const queryString = pathname.query
          ? `?${Object.keys(pathname.query)
              .map((key) => `${key}=${pathname.query[key]}`)
              .join('&')}`
          : '';
        navigate(`${pathname.pathname}${queryString}`, { replace });
      }
    }

    return {
      // For convenience add push(), replace(), pathname at top level
      push,
      replace,
      pathname: location.pathname,
      query: {
        ...queryString.parse(location.search), // Convert string to object
        ...params,
      },
      location,
      history: navigate,
    };
  }, [params, location, navigate]);
}
